import { environment } from "@environments/environment";
import { ConsentRequest, ConsentType, MeterDetailsFromConsentRequest, RelationshipType } from "@models/consent.model";
import { AddressLine, AddressResponse, ConsentStatus, ElecMeterDetails, FuelType, GasMeterDetails, Meter, MeterDetails, MeterPreview, MeterType } from "@models/meter.model";

export interface ConsentDateRange {
    startDate: Date;
    endDate: Date;
}

export interface ConsentExpiryRange {
    minExpiryDate: Date;
    maxExpiryDate: Date;
}

/**
 * Calculate consent date range where:
 * - startDate is 5 days before current date
 * - endDate is 1 year before startDate
 * @returns ConsentDateRange with startDate and endDate
 */
export function calculateConsentDateRange(): ConsentDateRange {
    const today = new Date();
    const endDate = new Date(today);
    endDate.setDate(today.getDate() - 5);
    
    const startDate = new Date(endDate);
    startDate.setFullYear(endDate.getFullYear() - 1);

    return { startDate, endDate };
}

/**
 * Calculate consent expiry date range where:
 * - minExpiryDate is 30 days from current date
 * - maxExpiryDate is 3 years from current date
 * @returns ConsentExpiryRange with minExpiryDate and maxExpiryDate
 */
export function getConsentExpiryRange(): ConsentExpiryRange {
    const today = new Date();
    
    const minExpiryDate = new Date(today);
    minExpiryDate.setDate(today.getDate() + 30); // 30 days from today
    
    const maxExpiryDate = new Date(today);
    maxExpiryDate.setFullYear(today.getFullYear() + 3); // 3 years from today

    return { minExpiryDate, maxExpiryDate };
}

export function calculateShareDateRange(): ConsentDateRange {
    const today = new Date();
    const startDate = new Date(today);
    
    const endDate = new Date(today);
    endDate.setFullYear(today.getFullYear() + 3); // Set end date to 3 years from today
    
    return { startDate, endDate };
}


export function addressMastertoAddress(addressMaster: AddressResponse): AddressLine {
    return {
        addressId: addressMaster.addressId,
        addressAsLine: addressMaster.address,
        postCode: addressMaster.postcode,
        numberMPAN: addressMaster.relatedData?.MPAN?.length || 0,
        numberMPRN: addressMaster.relatedData?.XID?.length || 0
    };
}

export function getMeterTypeForElecMeter(meterDetails: ElecMeterDetails): MeterType {
    if (meterDetails.meterType.startsWith('S')) {
        return MeterType.SMART;
    } else if (meterDetails.meterType === 'H') {
        return MeterType.HALF_HOURLY;
    } else if (meterDetails.profileClass === '00') {
        return MeterType.HALF_HOURLY;
    } else {
        return MeterType.NON_HALF_HOURLY; // Default to Non-HH for other types
    }
}

export function getMeterTypeForGasMeter(meterDetails: GasMeterDetails): MeterType {
    if (meterDetails.keyMeterType.startsWith('S')) {
        return MeterType.SMART;
    } else {
        return MeterType.NON_HALF_HOURLY;
    }
}

export function elecMeterMasterSearchToMeterDetails(elecMeterDetails: ElecMeterDetails): MeterDetails {
    return {
        meterNo: elecMeterDetails.mpanCore,
        fuelType: FuelType.ELEC,
        meterType: getMeterTypeForElecMeter(elecMeterDetails),
        addressId: elecMeterDetails.addressId,
        addressAsLine: elecMeterDetails.addressAsLine,
        currentSupplierName: elecMeterDetails.currentSupplierName || 'Unknown Supplier',
        postCode: elecMeterDetails.postCode,
        postTown: elecMeterDetails.postTown || ''
    };
}

export function gasMeterMasterSearchToMeterDetails(gasMeterDetails: GasMeterDetails): MeterDetails {
    return {
        meterNo: gasMeterDetails.MPRN,
        fuelType: FuelType.GAS,
        meterType: getMeterTypeForGasMeter(gasMeterDetails),
        addressId: gasMeterDetails.addressId,
        addressAsLine: gasMeterDetails.addressAsLine,
        currentSupplierName: gasMeterDetails.currentSupplierName || 'Unknown Supplier',
        postCode: gasMeterDetails.postCode,
        postTown: gasMeterDetails.postTown || ''
    };
}

export function capitalizeFirstLetter(str: string): string {
    if (!str) return '';
    str = str.trim().toLowerCase();
    return str.charAt(0).toUpperCase() + str.slice(1);
}

/**
 * Get short form of meter type for display
 * @param meterType The meter type enum value
 * @returns Short form string (HH, NHH, or SMART)
 */
export function getMeterTypeShortForm(meterType: MeterType | string): string {
    switch (meterType) {
        case MeterType.HALF_HOURLY:
        case 'Half Hourly':
            return 'HH';
        case MeterType.NON_HALF_HOURLY:
        case 'Non Half Hourly':
            return 'NHH';
        case MeterType.SMART:
        case 'Smart Meter':
            return 'SMART';
        default:
            return meterType?.toString().toUpperCase() || '';
    }
}

export function getRelationshipForLoa(relationship: RelationshipType): string {
    switch (relationship) {
        case RelationshipType.OCCUPIER:
            return 'Occupant';
        case RelationshipType.BILL_PAYER:
            return 'Energy bill payer';
        case RelationshipType.LANDLORD:
            return 'Landlord';
        default:
            return 'Energy bill payer';
    }
}

/**
 * Format a date as YYYY-MM-DD for API requests
 * @param date The date to format
 * @returns string The formatted date string
 * @throws Error if the date year is invalid (before 1900 or after 2100)
 */
export function formatDateForApi(date: Date): string {
    let year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    
    // Handle 2-digit year edge case: JavaScript Date with years 0-99 creates dates in years 0-99 AD
    // If year is between 0 and 99, it's likely a 2-digit year that should be in the 1900s or 2000s
    if (year >= 0 && year < 100) {
        // Assume years 00-30 are 2000s, 31-99 are 1900s (common convention)
        year = year <= 30 ? 2000 + year : 1900 + year;
        console.warn(`formatDateForApi: Corrected 2-digit year ${date.getFullYear()} to ${year}`);
    }
    
    // Validate year is within reasonable range
    if (year < 1900 || year > 2100) {
        console.error(`formatDateForApi: Invalid year detected: ${year}`, { 
            originalDate: date.toString(),
            originalYear: date.getFullYear()
        });
        throw new Error(`Invalid date: year ${year} is out of valid range (1900-2100)`);
    }
    
    return `${year}-${month}-${day}`;
}

/**
 * Format a date as DD/MM/YYYY for UK display format
 * @param date The date to format
 * @returns string The formatted date string in UK format
 */
export function formatDateForDisplay(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${day}/${month}/${year}`;
}

/**
 * Check if meter is a Smart meter
 * @param meterDetails The meter details to check
 * @returns boolean True if the meter is a Smart meter
 */
export function isSmartMeter(meterDetails: MeterDetails): boolean {
    return meterDetails?.meterType === MeterType.SMART;
}

/**
 * Check if meter is Half Hourly
 * @param meterDetails The meter details to check
 * @returns boolean True if the meter is Half Hourly
 */
export function isHHMeter(meterDetails: MeterDetails): boolean {
    return meterDetails?.meterType === MeterType.HALF_HOURLY;
}

/**
 * Check if meter is Non-Half Hourly
 * @param meterDetails The meter details to check
 * @returns boolean True if the meter is Non-Half Hourly
 */
export function isNonHHMeter(meterDetails: MeterDetails): boolean {
    return meterDetails?.meterType === MeterType.NON_HALF_HOURLY;
}

/**
 * Check if meter is supported (HH or Non-HH)
 * @param meterDetails The meter details to check
 * @returns boolean True if the meter is supported (HH or Non-HH)
 */
export function isSupportedMeter(meterDetails: MeterDetails): boolean {
    return isHHMeter(meterDetails) || isNonHHMeter(meterDetails);
}

/**
 * Check if the consent type is for smart meters
 * @param consentType The consent type to check
 * @returns boolean True if the consent type is for smart meters
 */
export function isSmartMeterConsentType(consentType: ConsentType): boolean {
    if (consentType === ConsentType.MAC_ADDRESS || consentType === ConsentType.BILL_UPLOAD ||
       consentType === ConsentType.STRIPE) {
      return true;
    }
    return false;
}

/**
 * Check if consent is required for a given meter type
 * @param meterType The meter type to check
 * @returns boolean True if consent is required
 */
export function requiresConsent(meterType: MeterType): boolean {
    return meterType === MeterType.HALF_HOURLY || meterType === MeterType.SMART || meterType === MeterType.NON_HALF_HOURLY;
}


//Write a function to compare two array of strings are same
export function areStringArraysEqual(arr1: string[], arr2: string[]): boolean {
    if (!arr1 || !arr2) return false;
    if (arr1.length !== arr2.length) return false;

    const sortedArr1 = [...arr1].sort();
    const sortedArr2 = [...arr2].sort();

    for (let i = 0; i < sortedArr1.length; i++) {
        if (sortedArr1[i] !== sortedArr2[i]) {
            return false;
        }
    }
    return true;
}

// Write a function to see if one array is a subset of another
export function isSubsetArray(subset: string[], superset: string[]): boolean {
    if (!subset || !superset) return false;
    if (subset.length > superset.length) return false;

    const supersetSet = new Set(superset);
    for (const item of subset) {
        if (!supersetSet.has(item)) {
            return false;
        }
    }
    return true;
}

// This function should take an array of parent meters and child meters and return the child meters that are not in parent meters
export function getChildMetersNotInParent(parentMeters: string[], childMeters: string[]): string[] {
    if (!parentMeters || !childMeters) return childMeters;

    const parentSet = new Set(parentMeters);
    return childMeters.filter(child => !parentSet.has(child));
}   


export function getMetersToShare(consentRequest: ConsentRequest): Meter[] {
    // Get all meter numbers from consent request
    const requestedMeters = consentRequest.meters;
    
    // Set start date to tomorrow
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    
    // Set expiry date to one year from today
    const oneYearFromToday = new Date();
    oneYearFromToday.setFullYear(oneYearFromToday.getFullYear() + 1);

    return requestedMeters.map(m => {
      return {
        meterNumber: m.mpxn,
        address: m.address || '',
        fuelType: m.fuelType as FuelType,
        meterType: m.type as MeterType,
        consentStatus: ConsentStatus.PENDING, // Default to PENDING for new meters
        numberOfShares: 0, // Default to 0 for new meters
        startDate: tomorrow, // Set start date to tomorrow
        expiryDate: oneYearFromToday // Set expiry date to one year from today
      } as Meter;
    });
}

export function getMeterTypeFromAbbreviation(abbreviation: string): MeterType {
  const mapping: { [key: string]: MeterType } = {
    'HH': MeterType.HALF_HOURLY,
    'NHH': MeterType.NON_HALF_HOURLY,
    'SMART': MeterType.SMART,
  };
  return mapping[abbreviation.trim().toUpperCase()] || MeterType.HALF_HOURLY;
}

export function getMeterDetailsPreview(
  meters: MeterDetailsFromConsentRequest[]
): MeterPreview[] {
  return meters.map((meter) => {
    // Convert string meterType to MeterType enum
    let meterTypeEnum = getMeterTypeFromAbbreviation(meter.type || 'HH');

    return {
      meterNumber: meter.mpxn,
      fuelType: meter.fuelType,
      meterType: meterTypeEnum,
      address: meter.address || 'Address not available',
    };
  });
}

  /**
   * Coerce any value to one of the supported relationship types, falling back to landlord
   */
  export function normalizeRelationshipType(relationshipType?: string | null): RelationshipType {
    return Object.values(RelationshipType).includes(relationshipType as RelationshipType)
      ? relationshipType as RelationshipType
      : RelationshipType.LANDLORD;
  }

  /**
   * Get display text for relationship type
   */
  export function getRelationshipDisplayText(relationshipType?: RelationshipType): string {
    const relationshipMapping: { [key in RelationshipType]: string } = {
      [RelationshipType.OCCUPIER] : 'Tenant or Occupier',
      [RelationshipType.BILL_PAYER]: 'Energy Bill Payer',
      [RelationshipType.LANDLORD]: 'Landlord or Leaseholder',
    };
    
    return relationshipMapping[normalizeRelationshipType(relationshipType)];
  }

  /**
   * Get icon for relationship type
   */
  export function getRelationshipIcon(relationshipType?: RelationshipType): string {
    const iconMapping: { [key in RelationshipType]: string } = {
      [RelationshipType.OCCUPIER]: 'home',
      [RelationshipType.BILL_PAYER]: 'home_work',
      [RelationshipType.LANDLORD]: 'person',
    };
    
    return iconMapping[normalizeRelationshipType(relationshipType)];
  }

    /**
   * Map frontend relationship type to API expected values
   */
  export function mapRelationshipTypeToApi(relationshipType: RelationshipType): string {
    const relationshipMapping: { [key in RelationshipType]: string } = {
      [RelationshipType.OCCUPIER]: 'Tenant',
      [RelationshipType.BILL_PAYER]: 'Owner_and_Bill_Payer',
      [RelationshipType.LANDLORD]: 'Owner',
    };
    
    return relationshipMapping[normalizeRelationshipType(relationshipType)];
  }

  export function mapRelationshipTypeFromApi(relationshipType?: string): RelationshipType {
    const relationshipMapping: { [key: string]: RelationshipType } = {
      'Tenant': RelationshipType.OCCUPIER,
      'Owner': RelationshipType.LANDLORD,
      'Owner_and_Bill_Payer': RelationshipType.BILL_PAYER,
      'Authorized_Representative': RelationshipType.LANDLORD,
    };
    return relationshipMapping[relationshipType ?? ''] ?? RelationshipType.LANDLORD;
  }

  /**
   * Convert a consent request meter to MeterDetails format
   * @param meter The meter from consent request
   * @returns MeterDetails object with default values for missing fields
   */
  export function mapConsentRequestMeterToMeterDetails(meter: MeterDetailsFromConsentRequest): MeterDetails {
    return {
      meterNo: meter.mpxn,
      meterType: getMeterTypeFromAbbreviation(meter.type || 'HH'),
      fuelType: meter.fuelType,
      addressId: '',
      addressAsLine: meter.address || 'ADDRESS NOT AVAILABLE',
      currentSupplierName: 'Unknown Supplier',
      postCode: 'Unknown',
      postTown: 'Unknown City'
    };
  }

  export function meterDetailsToMeter(meterDetails: MeterDetails, relationship: RelationshipType): Meter {
    return {
      meterNumber: meterDetails.meterNo,
      fuelType: meterDetails.fuelType,
      meterType: meterDetails.meterType,
      address: meterDetails.addressAsLine,
      consentStatus: ConsentStatus.PENDING, // Default to PENDING for new meters
      numberOfShares: 0, // Default to 0 for new meters
      startDate: new Date(), // Default to today
      expiryDate: new Date(new Date().setFullYear(new Date().getFullYear() + 1)), // Default to one year from today,
      relationship: relationship
    };
  }


export function getShareableLink(shareId: string): string {
  return  `${environment.openDataApiUrl}/core/v1/data-download?token=${shareId}`
}
